import { User } from 'models/user';
import { Organization } from 'models/organization';
import { TotalAmount, Invoice } from './invoice';
import { OrderStep, Time } from './workflow-step';
import { File as DenthubFile } from './file';
import { TransportOrder } from './transport';
import { OrderTag } from './tags';
import { OrderForm } from './order-form';
import { NShiftShipmentWaybill } from './nshift-shipment-waybill';
import { ScannerCase } from './scanner-case';
import { AllAdditionalInfo } from './order-input/shared';

export enum OrderOnDateFilter {
    NO_DATE_FILTER = 'NO_DATE_FILTER',
    ORDER_CREATED = 'ORDER_CREATED',
    ORDER_DELIVERED = 'ORDER_DELIVERED',
    ORDER_RECEIVED = 'ORDER_RECEIVED',
    ORDER_SENT = 'ORDER_SENT',
    DEADLINE = 'DEADLINE',
    DELIVERY_DATE = 'DELIVERY_DATE',
}

export type WorkFlow = {
    _id: string;
    timeInCurrentStep: Time;
    currentStep: OrderStep;
    stepLogs: OrderStep[];
};

export type Certified = {
    on: number;
    by: User;
};

export type LinkedUser = {
    user: User;
    dateAdded: number;
};

export type LinkedClinic = {
    clinic: Organization;
    dateAdded: number;
};

export enum InvoicingState {
    'NOT_READY' = 'NOT_READY',
    'READY_FOR_INVOICE' = 'READY_FOR_INVOICE',
    'SHOULD_NOT_INVOICE' = 'SHOULD_NOT_INVOICE',
    'INVOICED' = 'INVOICED',
}

export enum OrderRejectStatus {
    DECLINED = 'DECLINED',
    CANCELLED = 'CANCELLED',
}

type Archived = {
    by: string[];
    byCustomer: boolean;
    byLab: boolean;
};

export type Order = {
    _id: string;
    orderId: string;
    currency: string;
    attachedScannerCaseFiles?: ScannerCase[];
    additionalInfo?: string;
    scanCaseId?: string;
    internalOrderId: string;
    type: OrderType;
    status: OrderStatus;
    unreadMessagesCount: number;
    dentist: User;
    clinic: Organization;
    lab: Organization;
    supplier: Organization;
    customer: Organization;
    isSupplierOrder: boolean;
    deliveryDetails: OrderDeliveryDetails;
    created: {
        on: number;
        by: User;
    };
    originalCreationDate: Date;
    approvedBy?: {
        fullName: string;
        id: string;
    };
    draftCreatedAt?: number;
    draftUpdatedAt?: number;
    draftCreatedBy?: User;
    patient: OrderPatient;
    constructionMaterials: string[];
    isModify: boolean;
    labFlag: boolean;
    historicalComments: AllAdditionalInfo[];
    currentPickup?: TransportOrder;
    currentDelivery?: TransportOrder | NShiftShipmentWaybill | any;
    linkedExtTechs: LinkedUser[];
    linkedDentists: LinkedUser[];
    linkedClinics?: LinkedClinic[];
    customParticipantEmails?: string[];
    workFlow: WorkFlow;
    isCertified: boolean;
    certified: Certified;
    totalAmount?: TotalAmount;
    isProductionFinished: boolean;
    invoice: Invoice;
    invoicingState?: InvoicingState;
    invoiceActionTime?: Date | null;
    invoiceCreatedTime?: Date;
    invoicingStateChangedAt?: Date;
    invoicingStateChangedBy?: string | User;
    isCreatedByLab?: boolean;
    tempInvoiceNumber?: string;
    isInternalOrderData?: boolean;
    isOrderDeviations?: boolean;
    messages: OrderMessage[];
    isWarrantyOrder: boolean;
    linkedOrders?: LinkedOrder[];
    orderRef?: LinkedOrder;
    rejectStatus?: OrderRejectStatus;
    archived: Archived;
    rating: Rating;
    isRated: boolean;
    scannerCase?: ScannerCase;
    deliveryNotePdfUrl: string;
    transportTrackingLink?: string;
    version: number;
    originalOrder: Order;
    availableVersions: AvailableVersion[];
    filesUrl: DenthubFile[];
    // DD-2506: the unified restoration model.
    restorations: Restoration[];
    restorationTypes: RestorationType[];
    manualSelectedReceivedDate: Date;
    manualSelectedSentDate?: Date;
    orderTags?: OrderTag[];
    isCopyOrder?: boolean;
    iteration?: number;
    decline?: Decline[];
    cancel?: Cancel;
    isReadyForDelivery?: boolean;
    isDeliveryCreated?: boolean;
    isImportedOrder?: boolean;
    orderForms: OrderForm[];
    clinicOrderVersion: string;
    totalOriginalPrice?: number;
    shouldShowTruckButton?: boolean;
    isDraft?: boolean;
    actionSet: string;
    isClientEnabled: boolean;
    productionValueReports?: ProductionValueReport[];
    productionPlan?: {
        stepId: string;
        stepName: string;
        technicianId?: string;
        technicianName?: string;
        startDate?: Date | null;
        deadline?: Date | null;
        estimatedProductionHours?: number;
        estimatedMachineHours?: number;
        stepOrder?: number;
        allowOvernight?: boolean;
        startNextDay?: boolean;
        machineId?: string;
        departmentId?: string;
        affectedByQuantityModifier?: number;
        workUnits?: number;
    }[];
    panNumber?: string;
    panNumberDescription?: string;
    hasOrderFiles?: boolean;
    hasDeliveryNotePdf?: boolean;
};

export type prodAction = {
    actionId: string;
    name: string;
    number: string;
    productionDeductionValue?: number;
};

export type ProductionValueReport = {
    actions: prodAction[];
    templateName: string;
    steps: ProductionStep[];
};

export type ProductionStep = {
    step: string;
    department: string;
    technician?: string;
    technicians?: User[];
    valuePercent: number;
    stepValue: number;
    stepDeductionValue?: number;
};

export type Decline = {
    by: string;
    message: string;
    declineOn: number;
};

export type Cancel = {
    by: string;
    message: string;
    on: number;
};

// --- DD-2506 unified restoration model (read side) -------------------------------------
// (`RestorationType` is the enum declared further down in this file.)

export enum TeethSelectionType {
    TEETH = 'TEETH',
    RANGE = 'RANGE',
    JAW = 'JAW',
    NONE = 'NONE',
}

export enum RestorationJaw {
    UPPER_JAW = 'UPPER_JAW',
    LOWER_JAW = 'LOWER_JAW',
    BOTH_JAWS = 'BOTH_JAWS',
}

export enum ToothState {
    PONTIC = 'PONTIC',
    EXTRACTED = 'EXTRACTED',
    MISSING = 'MISSING',
}

export enum RestorationPropertyValueType {
    SIMPLE_VALUE = 'SIMPLE_VALUE',
    VALUE_AND_LABEL = 'VALUE_AND_LABEL',
    LIST_OF_TEETH = 'LIST_OF_TEETH',
    BOOLEAN_VALUE = 'BOOLEAN_VALUE',
}

export type RestorationPrice = {
    quantity: number;
    unitPrice: number;
    vatPercent: number;
    totalWithoutVat: number;
    totalWithVat: number;
    originalUnitPrice: number;
    originalTotalWithVat: number;
};

export type RestorationActionCatalogue = {
    exists: boolean;
    quotable?: boolean | null;
    internalCode?: string | null;
    materialMacros: { _id: string; name: string }[];
};

export type RestorationAction = {
    _id: string;
    /** @deprecated migration aid: pairs the row with its legacy added action until step 3 */
    actionId?: string | null;
    code?: string | null;
    name: string;
    restorationType?: RestorationType | null;
    price: RestorationPrice;
    warranty: boolean;
    isTioTandvard: boolean;
    category?: string | null;
    categoryName?: string | null;
    /** Frozen with the row: the action set's quotable flag. */
    quotable?: boolean | null;
    /** Frozen with the row: the quantity follows the teeth. */
    costPerTooth?: boolean | null;
    /** Frozen with the row: the supplying lab's internal code; null for the clinic. */
    internalCode?: string | null;
    catalogue?: RestorationActionCatalogue | null;
};

export type RestorationMaterial = {
    _id: string;
    /** The catalogue material the row was placed from; a free-text row has none. */
    materialId?: string | null;
    relatedActionId?: string | null;
    code?: string | null;
    name: string;
    batchNumber?: string | null;
    /** The chosen material's price difference kept as a row; not an added material. */
    isMaterialSurcharge?: boolean | null;
    warranty: boolean;
    isTioTandvard: boolean;
    price: RestorationPrice;
};

export type RestorationTeeth = {
    type: TeethSelectionType;
    selectedTeeth?: number[] | null;
    selectedRange?: { start: number; end: number } | null;
    selectedJaw?: RestorationJaw | null;
    toothStates?: { tooth: number; state: ToothState }[] | null;
};

export type RestorationActionUnit = {
    actionRowId: string;
    label: string;
    value: number;
};

export type RestorationProperty = {
    key: string;
    valueType: RestorationPropertyValueType;
    value: unknown;
    text?: string | null;
    flag?: boolean | null;
    teeth?: number[] | null;
    units?: RestorationActionUnit[] | null;
};

export type Restoration = {
    _id: string;
    type: RestorationType;
    actions: RestorationAction[];
    materials: RestorationMaterial[];
    teeth: RestorationTeeth;
    properties: RestorationProperty[];
    /** The restoration's own flags; unset on restorations derived from legacy orders. */
    warranty?: boolean | null;
    isTioTandvard?: boolean | null;
    legacyConstructionId?: string | null;
};

export type AvailableVersion = {
    version: number;
    createdAt: Date;
};

export type RatingReasons = {
    name: string;
    value: boolean;
};

export type Rating = {
    satisfied: boolean;
    notSatisfied: boolean;
    reasons: RatingReasons[];
    comments: string;
    by: User;
};

export type OrderMessage = {
    message: string;
    orderId?: string;
    type: string;
    deliveryDate?: number;
    readBy: string[];
    orderVersion: number;
    by: string;
    hasManualDateEntry?: boolean;
    messageOn: number;
    manualSelectedDate?: number;
};

export type OrderDeliveryDetails = {
    date: number;
    price: number;
    status: OrderDeliveryStatus;
    deadlineStatus?: OrderDeliveryStatus;
    sampleType: SampleType;
    sample?: Sample;
    isPickUpAtClinic: boolean;
    isLabHandleRequest: boolean;
    destination: Organization;
    pickUpLocation: Organization;
    isLabHaveOwnResource: boolean;
    reason?: Record<string, boolean>;
    time?: {
        hour?: number;
        minute?: number;
    };
    internalDeliveryDate: Date;
    externalDeliveryDate: Date;
};
export type Content = {
    name: string;
    value: boolean;
};

export type Sample = {
    type: string;
    otherContent: string;
    content?: Content[];
};

export type OrderPatient = {
    personalIdentityNumber: string;
    firstName: string;
    lastName: string;
    category?: string;
    noPersonalIdentityNumber?: boolean;
    gender?: string;
    patientCategory?: PatientCategory;
};

export type PatientCategory = {
    minage?: number;
    maxage?: number;
    name: string;
    translatedName?: string;
};

export enum OrderType {
    SIMPLE_TYPE = 'SIMPLE_TYPE',
    ACTION_BASED = 'ACTION_BASED',
}

export enum OrderStatus {
    ALL = 'ALL',
    ACCEPTED_BY_LAB = 'ACCEPTED_BY_LAB',
    CLINIC_RECEIVED_DELIVERY = 'CLINIC_RECEIVED_DELIVERY',
    ORDER_APPROVED = 'ORDER_APPROVED',
    SENT_BY_CUSTOMER = 'SENT_BY_CUSTOMER',
    SENT_BY_LAB = 'SENT_BY_LAB',
    CANCELLED = 'CANCELLED',
    DECLINED = 'DECLINED',
    DRAFT = 'DRAFT',
    COMPLETED_DRAFT = 'COMPLETED_DRAFT',
}

export enum OrderDeliveryStatus {
    NO_REMARKS = 'NO_REMARKS',
    EARLY = 'EARLY',
    DELAYED = 'DELAYED',
}

export enum SampleType {
    ALL = 'ALL',
    NO_SAMPLE = 'NO_SAMPLE',
    PHYSICAL = 'PHYSICAL',
    DIGITAL = 'DIGITAL',
}

export type LinkedOrder = {
    order: string;
    orderId: string;
    type: string;
};

export enum RestorationType {
    // Digital lab services
    CAD = 'CAD',
    CAM_MILLING = 'CAM_MILLING',
    SURGICAL_GUIDE = 'SURGICAL_GUIDE',
    LAB_SERVICES_OTHER = 'LAB_SERVICES_OTHER',

    // Fixed prosthetics
    BRIDGE = 'BRIDGE',
    CROWN = 'CROWN',
    CROWN_BRIDGE = 'CROWN_BRIDGE',
    INLAY = 'INLAY',
    ONLAY = 'ONLAY',
    POST_CORE = 'POST_CORE',
    TEMPORARY_BRIDGE = 'TEMPORARY_BRIDGE',
    TEMPORARY_CROWN = 'TEMPORARY_CROWN',
    TEMPORARY_INLAY = 'TEMPORARY_INLAY',
    TEMPORARY_ONLAY = 'TEMPORARY_ONLAY',
    VENEER = 'VENEER',
    FIXED_PROSTHETICS_OTHER = 'FIXED_PROSTHETICS_OTHER',

    // Implant prosthetics
    ABUTMENT = 'ABUTMENT',
    IMPLANT_BRIDGE = 'IMPLANT_BRIDGE',
    IMPLANT_CROWN = 'IMPLANT_CROWN',
    IMPLANT_OVERDENTURE = 'IMPLANT_OVERDENTURE',
    TEMPORARY_IMPLANT_BRIDGE = 'TEMPORARY_IMPLANT_BRIDGE',
    TEMPORARY_IMPLANT_CROWN = 'TEMPORARY_IMPLANT_CROWN',
    IMPLANT_PROSTHETICS_OTHER = 'IMPLANT_PROSTHETICS_OTHER',

    // Orthodontics
    ALIGNER = 'ALIGNER',
    ANCHORAGE = 'ANCHORAGE',
    EXPANSION_PALATAL = 'EXPANSION_PALATAL',
    GROWTH_ADAPTATION = 'GROWTH_ADAPTATION',
    RETAINER = 'RETAINER',
    ORTHODONTICS_OTHER = 'ORTHODONTICS_OTHER',

    // Removable prosthetics
    DIAGNOSTIC_WAXUP = 'DIAGNOSTIC_WAXUP',
    RELINE = 'RELINE',
    CLASP = 'CLASP',
    FLEXIBLE_PARTIAL = 'FLEXIBLE_PARTIAL',
    FULL_DENTURE = 'FULL_DENTURE',
    PARTIAL_DENTURE = 'PARTIAL_DENTURE',
    TEMPORARY_FULL_DENTURE = 'TEMPORARY_FULL_DENTURE',
    TEMPORARY_PARTIAL_DENTURE = 'TEMPORARY_PARTIAL_DENTURE',
    REMOVABLE_PROSTHETICS_OTHER = 'REMOVABLE_PROSTHETICS_OTHER',

    // Guards and splints
    SPORTS_GUARD = 'SPORTS_GUARD',
    ANTIAPNEA = 'ANTIAPNEA',
    BITE_REGISTRATION = 'BITE_REGISTRATION',
    BLEACH_TRAY = 'BLEACH_TRAY',
    NIGHT_GUARD = 'NIGHT_GUARD',
    SPLINT = 'SPLINT',
    GUARDS_AND_SPLINTS_OTHER = 'GUARDS_AND_SPLINTS_OTHER',
}
